import React, { useEffect, useState, useMemo } from 'react'
import { LeaseAuditController } from '../../../hooks/documentAudit/useLeaseAudit'
import {
    Theme,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    CircularProgress,
    Typography,
    IconButton,
    Button,
    TextField,
    Tooltip,
    Paper,
    MenuItem,
    Popover,
    Slide,
    Modal,
} from '@material-ui/core'
import { Container } from '../../../components'
import {
    getEntityClassificationUiName,
    classificationDataTypes,
} from '../../../models/DocumentAudit/services'
import { Autocomplete, Pagination } from '@material-ui/lab'
import AccountTreeIcon from '@material-ui/icons/AccountTree'
import PublishIcon from '@material-ui/icons/Publish'

import {
    EntityClass,
    EntityValue,
    EntityAlias,
    RentRollFile,
    DocumentBatch,
} from '../../../models/DocumentAudit/types'
import { useUser } from '../../../hooks'
import { isAccountManager } from '../../../helpers/stringHelpers'
import { DropFiles } from '../../../components/DropFiles'
import { RentRollDocumentManagerModal } from './RentRollDocumentManagerModal'
import { typographyStyle } from '../../../theme/typographyStyle'

interface Props {
    theme: Theme
    bodyH: number
    footerH: number
    leaseAuditController: LeaseAuditController
    isActive: boolean
}

const formatValue = (value: any, entityClassName: string | null): string => {
    if (value === null || value === undefined) return ''

    // If no classification mapping exists, use default formatting
    if (!entityClassName) return String(value)

    const dataType = classificationDataTypes.get(entityClassName as any)
    if (!dataType) return String(value)

    switch (dataType.format) {
        case 'date':
            return new Date(value).toLocaleDateString('en-US', {
                month: '2-digit',
                day: '2-digit',
                year: 'numeric',
            })
        case 'currency':
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
            }).format(Number(value))
        case 'number':
            return new Intl.NumberFormat('en-US').format(Number(value))
        case 'boolean':
            return value ? 'Yes' : 'No'
        default:
            return String(value)
    }
}

export const RentRollPage = (props: Props) => {
    const { theme, bodyH, footerH, leaseAuditController, isActive } = props

    const { workspaceUser } = useUser()
    const superUser = isAccountManager(workspaceUser)
    const [rowsPerPage, setRowsPerPage] = useState(25)
    const [offset, setOffset] = useState(0)
    const [excelViewerOpen, setExcelViewerOpen] = useState(false)
    const [
        rentRollDocumentManagerOpen,
        setRentRollDocumentManagerOpen,
    ] = useState(false)
    const [
        selectedEntityValue,
        setSelectedEntityValue,
    ] = useState<EntityValue | null>(null)
    const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLElement | null>(
        null,
    )
    const [selectedHeader, setSelectedHeader] = useState<EntityAlias | null>(
        null,
    )
    const [
        selectedEntityClass,
        setSelectedEntityClass,
    ] = useState<EntityClass | null>(null)
    const [sortConfig, setSortConfig] = useState<{
        key: string
        direction: 'asc' | 'desc' | null
    }>({ key: '', direction: null })

    const rentRolls = leaseAuditController.rentRolls
    const entityClasses = leaseAuditController.entityClasses

    // Extract headers from rentRolls data
    const headers = useMemo(() => {
        if (!rentRolls || rentRolls.length === 0) return []

        // Get unique alias objects from all rent rolls
        const aliasMap = new Map<string, EntityAlias>()

        rentRolls.forEach((rentRoll) => {
            if (rentRoll.entity_values) {
                rentRoll.entity_values.forEach((ev) => {
                    if (!aliasMap.has(ev.entity_alias.alias)) {
                        aliasMap.set(ev.entity_alias.alias, ev.entity_alias)
                    }
                })
            }
        })

        return Array.from(aliasMap.values())
    }, [rentRolls])

    // Create the table data structure
    const tableData = useMemo(() => {
        if (!rentRolls || rentRolls.length === 0 || headers.length === 0)
            return []

        return rentRolls.map((rentRoll) => {
            // Create a map for quick lookups
            const valueMap = new Map<string, EntityValue>()

            rentRoll.entity_values?.forEach((ev) => {
                valueMap.set(ev.entity_alias.alias, ev)
            })

            // Create a row with all headers
            const row: Record<
                string,
                { value: any; entityValue: EntityValue | null }
            > = {}
            headers.forEach((header) => {
                const entityValue = valueMap.get(header.alias) || null
                row[header.alias] = {
                    value: entityValue?.value || null,
                    entityValue,
                }
            })

            return row
        })
    }, [rentRolls, headers])

    // Apply sorting to the data
    const getSortedData = (data: any[]) => {
        if (!sortConfig.key || !sortConfig.direction) return data

        return [...data].sort((a, b) => {
            const headerAlias = sortConfig.key
            const aCell = a[headerAlias]
            const bCell = b[headerAlias]

            const aValue = aCell?.value ?? ''
            const bValue = bCell?.value ?? ''

            // Find the header to determine data type
            const header = headers.find((h) => h.alias === headerAlias)
            const dataType = header?.entity_class?.data_type || 'TEXT'

            // Sort based on data type
            if (dataType === 'NUMBER' || dataType === 'CURRENCY') {
                const aNum = Number(aValue) || 0
                const bNum = Number(bValue) || 0
                return sortConfig.direction === 'asc'
                    ? aNum - bNum
                    : bNum - aNum
            } else if (dataType === 'DATE') {
                const aDate = aValue ? new Date(aValue).getTime() : 0
                const bDate = bValue ? new Date(bValue).getTime() : 0
                return sortConfig.direction === 'asc'
                    ? aDate - bDate
                    : bDate - aDate
            } else {
                // Default string comparison
                const aStr = String(aValue)
                const bStr = String(bValue)
                return sortConfig.direction === 'asc'
                    ? aStr.localeCompare(bStr)
                    : bStr.localeCompare(aStr)
            }
        })
    }

    // Get sorted data
    const sortedData = useMemo(() => {
        return getSortedData(tableData)
    }, [tableData, sortConfig])

    // Simple pagination handlers - no API calls needed
    const handlePageChange = (
        event: React.ChangeEvent<unknown>,
        value: number,
    ) => {
        setOffset((value - 1) * rowsPerPage)
    }

    const handleRowsPerPageChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setOffset(0)
    }

    // Pagination calculations
    const totalPages = Math.ceil(sortedData.length / rowsPerPage)
    const currentPageData = sortedData.slice(offset, offset + rowsPerPage)

    // Function to handle opening the mapping popover
    const handleMapClassificationClick = (
        event: React.MouseEvent<HTMLElement>,
        header: EntityAlias,
    ) => {
        setPopoverAnchorEl(event.currentTarget)
        setSelectedHeader(header)
    }

    // Function to handle closing the popover
    const handlePopoverClose = () => {
        setPopoverAnchorEl(null)
        setSelectedHeader(null)
        setSelectedEntityClass(null)
    }

    // Function to save the entity class mapping
    const handleSaveMapping = () => {
        if (selectedHeader && selectedEntityClass && leaseAudit) {
            leaseAuditController
                .updateAliasEntityClassifications(
                    selectedHeader.id,
                    selectedEntityClass.id,
                )
                .then(() => {
                    // Refresh the data after mapping is saved
                    leaseAuditController.getRentRoll(leaseAudit.id)
                    handlePopoverClose()
                })
        } else {
            handlePopoverClose()
        }
    }

    // Handle sort click
    const handleSortClick = (headerAlias: string) => {
        let direction: 'asc' | 'desc' | null = 'asc'

        if (sortConfig.key === headerAlias) {
            if (sortConfig.direction === 'asc') direction = 'desc'
            else if (sortConfig.direction === 'desc') direction = null
            else direction = 'asc'
        }

        setSortConfig({ key: headerAlias, direction })
    }

    if (leaseAuditController.loading.fetchRentRolls) {
        return (
            <Container
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: bodyH,
                }}
            >
                <CircularProgress />
            </Container>
        )
    }

    const leaseAudit = leaseAuditController.leaseAudit
    if (leaseAudit === undefined || leaseAudit === null) {
        return null
    }

    return (
        <Container
            style={{
                overflow: 'auto',
                flexDirection: 'column',
                display: 'flex',
                height: bodyH + 75,
            }}
        >
            <TableContainer
                component={Paper}
                style={{
                    height: '100%',
                    flexDirection: 'column',
                    overflow: 'auto',
                    marginTop: theme.spacing(0),
                }}
            >
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            {headers.map((header) => (
                                <TableCell
                                    key={header.id}
                                    style={{
                                        position: 'sticky',
                                        top: 0,
                                        background:
                                            theme.palette.background.paper,
                                        zIndex: 1,
                                    }}
                                >
                                    <Container
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: theme.spacing(1),
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <Container
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: theme.spacing(1),
                                            }}
                                        >
                                            <Tooltip
                                                title={
                                                    header.entity_class
                                                        ? getEntityClassificationUiName(
                                                              header
                                                                  .entity_class
                                                                  .name,
                                                          )
                                                        : superUser
                                                        ? 'Click to Map Classification'
                                                        : ''
                                                }
                                            >
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            if (superUser) {
                                                                handleMapClassificationClick(
                                                                    e,
                                                                    header,
                                                                )
                                                            }
                                                        }}
                                                        disabled={!superUser}
                                                        style={{
                                                            padding: theme.spacing(
                                                                0.5,
                                                            ),
                                                        }}
                                                    >
                                                        <AccountTreeIcon
                                                            style={{
                                                                fontSize: 18,
                                                                color: header.entity_class
                                                                    ? theme
                                                                          .palette
                                                                          .grey[600]
                                                                    : theme
                                                                          .palette
                                                                          .error
                                                                          .main,
                                                            }}
                                                        />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </Container>

                                        <Typography
                                            style={{
                                                ...typographyStyle(
                                                    theme.typography.subtitle2,
                                                ),
                                                fontWeight:
                                                    theme.typography
                                                        .fontWeightBold,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() =>
                                                handleSortClick(header.alias)
                                            }
                                        >
                                            {getEntityClassificationUiName(
                                                header.alias,
                                            )}
                                            {sortConfig.key ===
                                                header.alias && (
                                                <span>
                                                    {sortConfig.direction ===
                                                    'asc'
                                                        ? ' ↑'
                                                        : ' ↓'}
                                                </span>
                                            )}
                                        </Typography>
                                    </Container>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {currentPageData.map((row, index) => (
                            <TableRow key={index + offset}>
                                {headers.map((header) => {
                                    const cell = row[header.alias]
                                    return (
                                        <TableCell
                                            style={{ textAlign: 'center' }}
                                            key={`${index + offset}-${
                                                header.alias
                                            }`}
                                            onClick={() => {
                                                if (cell.entityValue) {
                                                    setExcelViewerOpen(true)
                                                    setSelectedEntityValue(
                                                        cell.entityValue,
                                                    )
                                                }
                                            }}
                                        >
                                            {cell.value}
                                        </TableCell>
                                    )
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination controls */}
            <Container
                style={{
                    marginTop: theme.spacing(2),
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: theme.spacing(2),
                }}
            >
                <TextField
                    select
                    value={rowsPerPage}
                    onChange={handleRowsPerPageChange}
                    variant="outlined"
                    size="small"
                    style={{ width: 100 }}
                >
                    {[10, 25, 50, 100].map((option) => (
                        <MenuItem key={option} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </TextField>
                <Pagination
                    key="pagination"
                    page={offset / rowsPerPage + 1}
                    count={totalPages}
                    onChange={handlePageChange}
                    color="primary"
                />
                <Typography variant="body2" color="textSecondary">
                    {offset + 1} -{' '}
                    {Math.min(offset + rowsPerPage, sortedData.length)} of{' '}
                    {sortedData.length}
                </Typography>
                {superUser && (
                    <Tooltip title="Manage Rent Roll Documents">
                        <IconButton
                            size="small"
                            onClick={() => {
                                setRentRollDocumentManagerOpen(true)
                            }}
                        >
                            <PublishIcon />
                        </IconButton>
                    </Tooltip>
                )}
            </Container>

            {/* Classification Mapping Popover */}
            <Popover
                open={Boolean(popoverAnchorEl)}
                anchorEl={popoverAnchorEl}
                onClose={handlePopoverClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                <Paper style={{ padding: theme.spacing(2), width: 300 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        Map Classification for{' '}
                        <strong>
                            {selectedHeader &&
                                getEntityClassificationUiName(
                                    selectedHeader.alias,
                                )}
                        </strong>
                    </Typography>
                    <Autocomplete
                        id="classification-selector"
                        options={entityClasses || []}
                        getOptionLabel={(option) =>
                            getEntityClassificationUiName(option.name)
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Select Classification"
                                variant="outlined"
                                size="small"
                                fullWidth
                            />
                        )}
                        onChange={(_, newValue) =>
                            setSelectedEntityClass(newValue)
                        }
                        value={selectedEntityClass}
                        fullWidth
                    />
                    <Container
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginTop: theme.spacing(2),
                        }}
                    >
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={handlePopoverClose}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSaveMapping}
                            disabled={!selectedEntityClass}
                        >
                            Save Mapping
                        </Button>
                    </Container>
                </Paper>
            </Popover>

            {/* Excel Viewer Modal */}
            <ExcelViewer
                open={excelViewerOpen}
                onClose={() => setExcelViewerOpen(false)}
                ev={selectedEntityValue}
            />

            <RentRollDocumentManagerModal
                open={rentRollDocumentManagerOpen}
                onClose={() => setRentRollDocumentManagerOpen(false)}
                leaseAuditController={leaseAuditController}
            />
        </Container>
    )
}

interface ExcelViewerProps {
    open: boolean
    onClose: () => void
    ev: EntityValue | null
}

const ExcelViewer = (props: ExcelViewerProps) => {
    const { open, onClose, ev } = props
    const [error, setError] = useState(false)

    if (!ev) return null

    return (
        <Modal
            open={open}
            onClose={onClose}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Slide direction="up" in={open}>
                <Paper>
                    <Container
                        style={{
                            height: 'calc(100vh - 200px)',
                            width: 'calc(100vw - 400px)',
                            display: 'flex',
                            overflow: 'hidden',
                            marginLeft: 'auto',
                            marginRight: 'auto',
                            flexDirection: 'column',
                            flex: 1,
                        }}
                    >
                        {error ? (
                            <Container
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: 20,
                                    textAlign: 'center',
                                }}
                            >
                                <Typography
                                    variant="h6"
                                    color="error"
                                    gutterBottom
                                >
                                    Unable to load file preview
                                </Typography>
                                <Typography variant="body2">
                                    The file may require authentication or is
                                    not publicly accessible.
                                </Typography>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    href={ev.document.file}
                                    target="_blank"
                                    style={{ marginTop: 16 }}
                                >
                                    Download File
                                </Button>
                            </Container>
                        ) : (
                            <iframe
                                key={`Excel-${ev.id}`}
                                id="excel-viewer"
                                width="100%"
                                height="100%"
                                loading="lazy"
                                src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                                    ev.document.file,
                                )}&activeCell=${ev.coordinates}`}
                                onError={() => setError(true)}
                            />
                        )}
                    </Container>
                </Paper>
            </Slide>
        </Modal>
    )
}
